import React, { useState, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { theme } from '../theme';
import reliefClimatData from '../data/reliefClimat.json';
import dairasExceptions from '../data/dairasClimatExceptions.json';
import wilayasIndex from '../data/wilayas.json';
import SectionModal from './SectionModal';

// Déclaration globale pour Google Maps
declare global {
  interface Window {
    google: any;
    initMap: () => void;
  }
}

const MapContainer = styled.div`
  width: 100%;
  height: 80vh;
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
`;

const MapTitle = styled.h2`
  position: absolute;
  top: 20px;
  left: 20px;
  color: ${theme.colors.outchou_green};
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  font-family: ${theme.fonts.title};
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  background: rgba(255, 255, 255, 0.9);
  padding: 10px 15px;
  border-radius: 8px;
`;

const Legend = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  background: white;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  max-height: 60vh;
  overflow-y: auto;
  z-index: 1000;
  max-width: 250px;
`;

const LegendTitle = styled.h3`
  color: ${theme.colors.outchou_green};
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 12px 0;
  font-family: ${theme.fonts.title};
`;

const LegendItem = styled.div<{ color: string; isHovered: boolean }>`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  transition: all 0.2s ease;
  background-color: ${props => props.isHovered ? '#f0f0f0' : 'transparent'};
  
  &:hover {
    background-color: #f0f0f0;
  }
`;

const LegendColor = styled.div<{ color: string }>`
  width: 16px;
  height: 16px;
  background-color: ${props => props.color};
  border-radius: 50%;
  margin-right: 10px;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const LegendName = styled.span`
  font-size: 12px;
  color: #333;
  font-weight: 500;
  font-family: ${theme.fonts.title};
`;

// Fonction pour obtenir l'image d'une région
const getRegionImage = (regionId: number): string => {
  const imageMap: { [key: number]: string } = {
    1: '/shared_assets/images/relief/le tell.jpg',
    2: '/shared_assets/images/relief/les hauts plateaux.jpg',
    3: '/shared_assets/images/relief/l\'atlas saharien.jpeg',
    4: '/shared_assets/images/relief/le sahara.jpg'
  };
  return imageMap[regionId] || '/shared_assets/images/relief/le tell.jpg';
};

// Fonction pour obtenir la légende d'une région
const getRegionImageCaption = (regionId: number): string | undefined => {
  const captionMap: { [key: number]: string } = {
    1: 'Aperçu de Constantine, perchée sur ses gorges.',
    2: 'Paysage des Hauts Plateaux avec l’Atlas saharien en toile de fond.',
    3: 'Vue sur la chaîne de l’Atlas saharien depuis Mécheria.',
    4: 'Panorama sur les formations rocheuses du Tassili n’Ahaggar (Hoggar).'
  };
  return captionMap[regionId];
};

// Fonction pour convertir les données de région en format sections pour le modal
const convertRegionToSections = (region: any): { [key: string]: string[] | string } => {
  const sections: { [key: string]: string[] | string } = {};
  
  if (region.location) {
    sections['Localisation'] = region.location;
  }
  
  if (region.relief) {
    sections['Relief'] = region.relief;
  }
  
  if (region.climat) {
    sections['Climat'] = region.climat;
  }
  
  if (region.temperature) {
    sections['Température'] = region.temperature;
  }
  
  if (region.precipitations) {
    sections['Précipitations'] = region.precipitations;
  }
  
  if (region.characteristics) {
    sections['Caractéristiques'] = region.characteristics;
  }
  
  if (region.wilayas && region.wilayas.length > 0) {
    sections['Wilayas'] = region.wilayas;
  }
  
  if (region.zones_specifiques && region.zones_specifiques.length > 0) {
    sections['Zones spécifiques'] = region.zones_specifiques;
  }
  
  if (region.massifs && region.massifs.length > 0) {
    sections['Massifs'] = region.massifs;
  }
  
  if (region.zones_climatiques && region.zones_climatiques.length > 0) {
    sections['Zones climatiques'] = region.zones_climatiques;
  }
  
  return sections;
};

const ReliefClimatMap: React.FC = () => {
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalWidth, setModalWidth] = useState<number | null>(null);
  const [isPinned, setIsPinned] = useState(false); // État pour savoir si la card est fixée par un clic
  const [isMobile, setIsMobile] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [wilayasWithHolesGeoData, setWilayasWithHolesGeoData] = useState<any>(null);
  const [exceptionDairasGeoData, setExceptionDairasGeoData] = useState<any>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<any>(null);
  const isPinnedRef = useRef(false);
  
  // Créer un Map des régions par ID pour lookup O(1) au lieu de O(n) avec .find()
  const regionsMap = useMemo(() => {
    const map = new Map<number, any>();
    reliefClimatData.regions.forEach(region => {
      map.set(region.id, region);
    });
    return map;
  }, []);

  // Charger les fichiers GeoJSON pré-calculés (dans data pour ne plus jamais avoir à recalculer)
  useEffect(() => {
    const timestamp = Date.now();
    
    // Charger wilayas_with_holes.geojson
    fetch(`/data/wilayas_with_holes.geojson?_=${timestamp}`, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        setWilayasWithHolesGeoData(data);
      })
      .catch(error => {
        console.error('Erreur lors du chargement de wilayas_with_holes.geojson:', error);
      });

    // Charger exception_dairas.geojson
    fetch(`/data/exception_dairas.geojson?_=${timestamp}`, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        setExceptionDairasGeoData(data);
      })
      .catch(error => {
        console.error('Erreur lors du chargement de exception_dairas.geojson:', error);
      });
  }, []);

  useEffect(() => {
    // Détecter si on est sur mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    // Charger Google Maps API
    if (!window.google && !document.querySelector('script[src*="maps.googleapis.com"]')) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyDgmbAXJ9fkmwRlYvg7ygBLsgJ6TP2bp1w&libraries=geometry`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
      
      script.onload = () => {
        if (wilayasWithHolesGeoData && exceptionDairasGeoData) {
          initMap();
        }
      };
    } else if (window.google && wilayasWithHolesGeoData && exceptionDairasGeoData) {
      initMap();
    }

    function initMap() {
      if (mapRef.current && wilayasWithHolesGeoData && exceptionDairasGeoData) {
        // Nettoyer la carte existante si elle existe
        if (mapInstance.current) {
          // Supprimer toutes les features existantes
          mapInstance.current.data.forEach((feature: any) => {
            mapInstance.current.data.remove(feature);
          });
          // Réinitialiser la carte
          mapInstance.current = null;
        }
        
        mapInstance.current = new window.google.maps.Map(mapRef.current, {
          center: { lat: 28.0339, lng: 1.6596 },
          zoom: 5,
          mapTypeId: 'satellite',
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          zoomControl: false,
          scaleControl: false,
          rotateControl: false,
          panControl: false,
          overviewMapControl: false,
          scrollwheel: true,
          disableDoubleClickZoom: false,
          keyboardShortcuts: true,
          gestureHandling: 'greedy',
          clickableIcons: false,
          styles: [
            {
              featureType: 'all',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            }
          ]
        });

            // ÉTAPE 1 : Charger les wilayas pré-calculées avec trous (déjà modifiées)
            if (wilayasWithHolesGeoData && reliefClimatData && (wilayasWithHolesGeoData as any).features) {
              // Ajouter les wilayas pré-calculées à la carte (elles contiennent déjà les trous)
              mapInstance.current.data.addGeoJson(wilayasWithHolesGeoData);
            }
          
            // ÉTAPE 2 : Charger les daïras d'exception pré-calculées (déjà avec leurs propriétés)
            if (exceptionDairasGeoData && (exceptionDairasGeoData as any).features) {
              // Ajouter les daïras d'exception au calque (elles ont déjà les propriétés regionId et isExceptionDaira)
              mapInstance.current.data.addGeoJson(exceptionDairasGeoData);
            }
          
          // Variable partagée pour gérer les clics sur les features
          let clickHandledByFeature = false;
          
            // ÉTAPE 3 : Appliquer le style UNIFIÉ immédiatement après l'ajout des GeoJSON
            // Plus besoin de setTimeout car addGeoJson() est synchrone et les features sont immédiatement disponibles
            // Les daïras d'exception et les wilayas doivent avoir EXACTEMENT le même style
            // (même opacité, même strokeWeight, etc.) pour que seule la couleur diffère
            
            // Forcer la réinitialisation du style en le supprimant d'abord
            mapInstance.current.data.setStyle(null);
            
            mapInstance.current.data.setStyle((feature: any) => {
              const featureName = feature.getProperty('name') || feature.getProperty('name:fr');
              if (!featureName) {
                return {
                  fillColor: 'transparent',
                  fillOpacity: 0,
                  strokeColor: 'transparent',
                  strokeWeight: 0,
                  strokeOpacity: 0
                };
              }
              
              // PRIORITÉ 1 : Vérifier si c'est une daïra d'exception (avec sa couleur spécifique)
              // Les daïras d'exception doivent avoir EXACTEMENT le même style que les wilayas
              // (même opacité, même strokeWeight, etc.) pour que seule la couleur diffère
              const isExceptionDaira = feature.getProperty('isExceptionDaira');
              if (isExceptionDaira) {
                const exceptionRegionId = feature.getProperty('regionId');
                if (exceptionRegionId) {
                  const region = regionsMap.get(exceptionRegionId);
                  if (region) {
                    // Style IDENTIQUE aux wilayas pour qu'on ne voie pas de différence
                    return {
                      fillColor: region.color,
                      fillOpacity: 0.6, // EXACTEMENT la même opacité que les wilayas
                      strokeColor: region.color,
                      strokeWeight: 2, // EXACTEMENT le même strokeWeight que les wilayas
                      strokeOpacity: 0.8 // EXACTEMENT la même strokeOpacity que les wilayas
                    };
                  }
                }
              }
              
              // Vérifier si c'est une daïra (sans exception)
              const isDaira = featureName.toLowerCase().includes('daïra');
              if (isDaira && !isExceptionDaira) {
                // Daïras sans exception : transparentes (ne pas afficher)
                return {
                  fillColor: 'transparent',
                  fillOpacity: 0,
                  strokeColor: 'transparent',
                  strokeWeight: 0,
                  strokeOpacity: 0
                };
              }
              
              // PRIORITÉ 2 : Styler les wilayas selon leur région en utilisant la propriété climat pré-calculée
              // Les wilayas ont maintenant la propriété climat directement dans le GeoJSON
              // Plus besoin de matching de noms ! C'est beaucoup plus rapide O(1) au lieu de O(n)
              const climatId = feature.getProperty('climat');
              
              // Vérifier si climatId existe et n'est pas 0 (0 signifie non assigné)
              if (climatId !== null && climatId !== undefined && climatId !== 0) {
                const region = regionsMap.get(climatId);
                if (region) {
                  // Vérifier si cette wilaya a des trous (daïras d'exception)
                  const hasExceptionHoles = feature.getProperty('_hasExceptionHoles');
                  
                  if (hasExceptionHoles) {
                    // Pour les wilayas avec des trous, rendre les contours transparents
                    // car les daïras d'exception vont dessiner leurs propres contours
                    return {
                      fillColor: region.color,
                      fillOpacity: 0.6, // EXACTEMENT la même opacité que les daïras d'exception
                      strokeColor: 'transparent', // Contours transparents pour éviter la superposition
                      strokeWeight: 0, // Pas de contour pour éviter la superposition
                      strokeOpacity: 0 // Complètement transparent
                    };
                  } else {
                    // Pour les wilayas sans trous, style normal avec contours
                    return {
                      fillColor: region.color,
                      fillOpacity: 0.6, // EXACTEMENT la même opacité que les daïras d'exception
                      strokeColor: region.color,
                      strokeWeight: 2, // EXACTEMENT le même strokeWeight que les daïras d'exception
                      strokeOpacity: 0.8 // EXACTEMENT la même strokeOpacity que les daïras d'exception
                    };
                  }
                }
              }
              
              // Style par défaut pour les features non assignées
              // Si c'est une daïra qui n'a pas d'exception, ne pas l'afficher (transparent)
              if (isDaira) {
                // Daïra sans exception : complètement transparente (ne pas afficher)
                return {
                  fillColor: 'transparent',
                  fillOpacity: 0,
                  strokeColor: 'transparent',
                  strokeWeight: 0,
                  strokeOpacity: 0
                };
              }
              
              // Pour les autres features (wilayas non assignées), style par défaut complètement transparent
              return {
                fillColor: 'transparent',
                fillOpacity: 0,
                strokeColor: 'transparent',
                strokeWeight: 0,
                strokeOpacity: 0
              };
            });
            
            // Attacher les événements immédiatement après l'ajout des GeoJSON
            // Plus besoin de setTimeout car les features sont déjà disponibles
            
            // Plus besoin de fonction findRegionFromFeature - on utilise directement les propriétés des features !
            
            // Événement de clic sur les zones de régions (pour web et mobile)
            mapInstance.current.data.addListener('click', (event: any) => {
                clickHandledByFeature = true;
                const clickedFeature = event.feature;
                const featureName = clickedFeature.getProperty('name') || clickedFeature.getProperty('name:fr');
                
                // PRIORITÉ 1 : Vérifier si c'est une daïra d'exception (utiliser les propriétés de la feature)
                const isExceptionDaira = clickedFeature.getProperty('isExceptionDaira');
                let region = null;
                
                if (isExceptionDaira) {
                  const exceptionRegionId = clickedFeature.getProperty('regionId');
                  if (exceptionRegionId) {
                    region = regionsMap.get(exceptionRegionId);
                  }
                }
                
                // PRIORITÉ 2 : Si ce n'est pas une daïra d'exception, utiliser la propriété climat pré-calculée
                if (!region) {
                  const climatId = clickedFeature.getProperty('climat');
                  if (climatId !== null && climatId !== undefined && climatId !== 0) {
                    region = regionsMap.get(climatId);
                  }
                }
                
                if (region) {
                  // console.log('Clic sur la région:', region.name);
                  handleOpenModal(region);
                }
                
                // Réinitialiser après un court délai
                setTimeout(() => {
                  clickHandledByFeature = false;
                }, 100);
              });
              
              // Événement de clic sur la carte (en dehors des zones)
              mapInstance.current.addListener('click', (event: any) => {
                // Attendre un peu pour voir si le clic a été géré par une feature
                setTimeout(() => {
                  if (!clickHandledByFeature && isPinnedRef.current) {
                    // Le clic n'a pas été sur une zone de région, fermer la card
                    handleCloseModal();
                  }
                }, 150);
            });
            
            // Désactiver le hover - le modal s'ouvre uniquement au clic
            // Le hover causait des bugs, donc on le désactive complètement
            
            // Ajouter les noms des régions sur la carte (utilisant les centres pré-calculés)
            // Utiliser requestAnimationFrame pour s'assurer que la carte est rendue avant d'ajouter les labels
            requestAnimationFrame(() => {
              reliefClimatData.regions.forEach(region => {
                if (region.center) {
                  // Créer un marqueur avec le nom de la région
                  new window.google.maps.Marker({
                    position: { lat: region.center.lat, lng: region.center.lng },
                    map: mapInstance.current,
                    title: region.name,
                    label: {
                      text: region.name,
                      color: 'white',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      fontFamily: 'Clash Display, sans-serif',
                      className: 'region-label'
                    },
                    icon: {
                      path: window.google.maps.SymbolPath.CIRCLE,
                      scale: 0,
                      fillColor: 'transparent',
                      fillOpacity: 0,
                      strokeColor: 'transparent',
                      strokeWeight: 0
                    }
                  });
                }
              });
            });
            
            // Ajout des noms des wilayas désactivé pour améliorer les performances
            // Décommentez le code ci-dessous si vous voulez les afficher pour débugger
            /*
            setTimeout(() => {
              console.log('Ajout des noms des wilayas sur la carte pour débugger...');
              
              // Fonction pour calculer le centre d'une géométrie
              const getCenterFromGeometry = (geometry: any): { lat: number; lng: number } | null => {
                if (!geometry) return null;
                
                let allLngs: number[] = [];
                let allLats: number[] = [];

                const extractCoordinates = (coords: any) => {
                  if (Array.isArray(coords[0])) {
                    coords.forEach((ring: any) => {
                      if (Array.isArray(ring[0])) {
                        extractCoordinates(ring);
                      } else if (Array.isArray(ring[0][0])) {
                        ring.forEach((polygon: any) => extractCoordinates(polygon));
                      } else {
                        ring.forEach((coord: any) => {
                          allLngs.push(coord[0]);
                          allLats.push(coord[1]);
                        });
                      }
                    });
                  } else {
                    allLngs.push(coords[0]);
                    allLats.push(coords[1]);
                  }
                };
                
                if (geometry.type === 'Polygon') {
                  extractCoordinates(geometry.coordinates);
                } else if (geometry.type === 'MultiPolygon') {
                  geometry.coordinates.forEach((polygon: any) => extractCoordinates(polygon));
                } else {
                  return null;
                }
                
                if (allLngs.length === 0 || allLats.length === 0) return null;
                
                const avgLng = allLngs.reduce((a, b) => a + b, 0) / allLngs.length;
                const avgLat = allLats.reduce((a, b) => a + b, 0) / allLats.length;
                
                return { lat: avgLat, lng: avgLng };
              };
              
              mapInstance.current.data.forEach((feature: any) => {
                const featureName = feature.getProperty('name') || feature.getProperty('name:fr');
                if (!featureName) return;
                if (featureName.toLowerCase().includes('daïra')) return;
                const hasExceptionHoles = feature.getProperty('_hasExceptionHoles');
                if (hasExceptionHoles) return;
                
                const geometry = feature.getGeometry();
                if (!geometry) return;
                
                const geojsonFeature = mapInstance.current.data.toGeoJson(feature);
                const geojsonGeometry = geojsonFeature?.geometry || geojsonFeature?.features?.[0]?.geometry;
                const center = geojsonGeometry ? getCenterFromGeometry(geojsonGeometry) : null;
                
                if (center) {
                  new window.google.maps.Marker({
                    position: { lat: center.lat, lng: center.lng },
                    map: mapInstance.current,
                    title: featureName,
                    label: {
                      text: featureName,
                      color: '#333',
                      fontSize: '11px',
                      fontWeight: 'normal',
                      className: 'wilaya-label'
                    },
                    icon: {
                      path: window.google.maps.SymbolPath.CIRCLE,
                      scale: 0,
                      fillColor: 'transparent',
                      fillOpacity: 0,
                      strokeColor: 'transparent',
                      strokeWeight: 0
                    },
                    zIndex: 999
                  });
                }
              });
            }, 2500);
            */
        }
      }
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', checkMobile);
      // Nettoyer la carte au démontage
      if (mapInstance.current) {
        mapInstance.current.data.forEach((feature: any) => {
          mapInstance.current.data.remove(feature);
        });
        mapInstance.current = null;
      }
    };
  }, [isMobile, wilayasWithHolesGeoData, exceptionDairasGeoData, regionsMap]);


  const toggleFullscreen = async () => {
    try {
      if (!isFullscreen) {
        if (mapRef.current?.requestFullscreen) {
          await mapRef.current.requestFullscreen();
        }
      } else {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
          await (document as any).webkitExitFullscreen();
        } else if ((document as any).mozCancelFullScreen) {
          await (document as any).mozCancelFullScreen();
        } else if ((document as any).msExitFullscreen) {
          await (document as any).msExitFullscreen();
        }
      }
    } catch (error) {
      console.error('Erreur plein écran:', error);
    }
  };

  // Gérer les événements de changement de plein écran
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullscreenActive = !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement
      );
      setIsFullscreen(isFullscreenActive);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
  };
  }, []);

  const getCurrentRegion = () => {
    if (!selectedRegion) return null;
    const regionId = parseInt(selectedRegion, 10);
    return regionsMap.get(regionId) || null;
  };

  const handleOpenModal = (region: any) => {
    setSelectedRegion(region.id.toString());
    setIsPinned(true);
    isPinnedRef.current = true;
    
    // Précharger l'image et calculer la largeur avant d'ouvrir le modal
    const imageUrl = getRegionImage(region.id);
    const img = new Image();
    img.onload = () => {
      const maxHeight = 500;
      const aspectRatio = img.width / img.height;
      const calculatedWidth = Math.min(img.width, maxHeight * aspectRatio);
      setModalWidth(calculatedWidth + 40); // +40 pour le padding et les bordures
      // Ouvrir le modal une fois la largeur calculée
      setIsModalOpen(true);
    };
    img.src = imageUrl;
  };

  const handleCloseModal = () => {
    setSelectedRegion(null);
    setIsModalOpen(false);
    setModalWidth(null);
    setIsPinned(false);
    isPinnedRef.current = false; // Synchroniser la ref
  };

  // Synchroniser la ref avec l'état isPinned
  useEffect(() => {
    isPinnedRef.current = isPinned;
  }, [isPinned]);

  // Mettre à jour les styles de la carte quand la région sélectionnée change
  useEffect(() => {
    if (mapInstance.current && reliefClimatData) {
      const currentSelectedRegion = selectedRegion ? regionsMap.get(parseInt(selectedRegion, 10)) : null;
      
      // Plus besoin de fonction findRegionFromFeature - on utilise directement les propriétés des features !
      
      mapInstance.current.data.setStyle((feature: any) => {
        const featureName = feature.getProperty('name') || feature.getProperty('name:fr');
        if (!featureName) {
          return {
            fillColor: 'transparent',
            fillOpacity: 0,
            strokeColor: 'transparent',
            strokeWeight: 0,
            strokeOpacity: 0
          };
        }
        
        // PRIORITÉ 1 : Vérifier si c'est une daïra d'exception (avec sa couleur spécifique)
        // Utiliser le même style unifié que dans ÉTAPE 3
        const isExceptionDaira = feature.getProperty('isExceptionDaira');
        if (isExceptionDaira) {
          const exceptionRegionId = feature.getProperty('regionId');
          if (exceptionRegionId) {
            const region = regionsMap.get(exceptionRegionId);
            if (region) {
              const isSelected = currentSelectedRegion && region.id === currentSelectedRegion.id;
              // Style IDENTIQUE aux wilayas avec possibilité d'accentuation si sélectionnée
              return {
                fillColor: region.color,
                fillOpacity: isSelected ? 0.8 : 0.6, // EXACTEMENT la même opacité que les wilayas
                strokeColor: region.color,
                strokeWeight: isSelected ? 4 : 2, // EXACTEMENT le même strokeWeight que les wilayas
                strokeOpacity: isSelected ? 1 : 0.8 // EXACTEMENT la même strokeOpacity que les wilayas
              };
            }
          }
        }
        
        // PRIORITÉ 2 : Sinon, vérifier si c'est une wilaya (seulement si ce n'est PAS une daïra)
        const isDaira = featureName.toLowerCase().includes('daïra');
        if (!isDaira) {
          // Utiliser directement la propriété climat pré-calculée - plus besoin de matching de noms !
          const climatId = feature.getProperty('climat');
          const region = (climatId !== null && climatId !== undefined && climatId !== 0)
            ? regionsMap.get(climatId)
            : null;
          if (region) {
            const isSelected = currentSelectedRegion && region.id === currentSelectedRegion.id;
            // Vérifier si cette wilaya a des trous (daïras d'exception)
            const hasExceptionHoles = feature.getProperty('_hasExceptionHoles');
            
            if (hasExceptionHoles) {
              // Pour les wilayas avec des trous, rendre les contours transparents
              // car les daïras d'exception vont dessiner leurs propres contours
              return {
                fillColor: region.color,
                fillOpacity: isSelected ? 0.8 : 0.6, // EXACTEMENT la même opacité que les daïras d'exception
                strokeColor: 'transparent', // Contours transparents pour éviter la superposition
                strokeWeight: 0, // Pas de contour pour éviter la superposition
                strokeOpacity: 0 // Complètement transparent
              };
            } else {
              // Pour les wilayas sans trous, style normal avec contours
              return {
                fillColor: region.color,
                fillOpacity: isSelected ? 0.8 : 0.6, // EXACTEMENT la même opacité que les daïras d'exception
                strokeColor: region.color,
                strokeWeight: isSelected ? 4 : 2, // EXACTEMENT le même strokeWeight que les daïras d'exception
                strokeOpacity: isSelected ? 1 : 0.8 // EXACTEMENT la même strokeOpacity que les daïras d'exception
              };
            }
          }
        }
        
        // Style par défaut : daïras sans exception = transparent, autres features = discret
        if (isDaira) {
          // Daïra sans exception : complètement transparente
          return {
            fillColor: 'transparent',
            fillOpacity: 0,
            strokeColor: 'transparent',
            strokeWeight: 0,
            strokeOpacity: 0
          };
        }
        
        // Autres features non assignées : style discret
        return {
          fillColor: 'transparent',
          fillOpacity: 0,
          strokeColor: 'transparent',
          strokeWeight: 0,
          strokeOpacity: 0
        };
      });
    }
  }, [selectedRegion]);


  const currentRegion = getCurrentRegion();


  return (
    <MapContainer className="map-container" key="relief-climat-map">
      {/* Bouton plein écran */}
          <button 
        onClick={toggleFullscreen}
            style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 1000,
          background: 'rgba(255, 255, 255, 0.95)',
          border: '2px solid rgba(0, 0, 0, 0.1)',
          borderRadius: '8px',
          padding: '0',
              cursor: 'pointer',
          fontSize: '16px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          width: '40px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s ease',
          backdropFilter: 'blur(8px)',
          fontWeight: 'bold',
          color: '#333'
            }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
          e.currentTarget.style.transform = 'scale(1.05)';
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)';
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
        }}
        title={isFullscreen ? 'Sortir du plein écran' : 'Plein écran'}
      >
        {isFullscreen ? '⤢' : '⤡'}
          </button>

      {/* Google Maps avec contours des wilayas */}
      <div ref={mapRef} style={{ width: '100%', height: '100%' }} />

      {/* Modal pour afficher les informations de la région */}
      {currentRegion && (
        <SectionModal
          title={currentRegion.name}
          subtitle={currentRegion.location}
          imageUrl={getRegionImage(currentRegion.id)}
          imageCaption={getRegionImageCaption(currentRegion.id)}
          sections={convertRegionToSections(currentRegion)}
          color={currentRegion.color}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          modalWidth={modalWidth}
        />
      )}
    </MapContainer>
  );
};

export default ReliefClimatMap;
